import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Registers the service worker, and prompts when a new build is waiting.
 *
 * ── WHY THIS REPLACED VERSION POLLING ───────────────────────────────────────
 * This used to fetch /version.json on a timer and offer `location.reload()`.
 * That works for a browser tab and does not work for an installed app: once a
 * service worker is serving the shell from its cache, a reload re-reads the
 * SAME cached bundle. The page would have prompted forever and never updated.
 *
 * So the worker is now the source of truth for "there is a new build":
 *
 *   1. useRegisterSW registers /sw.js and re-checks it on the interval below.
 *   2. A new worker installs and WAITS — src/sw.ts only calls skipWaiting when
 *      this component asks it to.
 *   3. needRefresh flips true, and we read /version.json purely to show the
 *      version number in the dialog.
 *   4. Update → updateServiceWorker(true) posts SKIP_WAITING; the new worker
 *      activates, `controllerchange` fires and vite-plugin-pwa reloads the page
 *      onto the new bundle.
 *
 * The Update button always reaches a reload, on a timer, even if the worker
 * never hands over — see the click handler. A disabled button that waits on an
 * event that may not arrive is a dead end, and on an installed app the only way
 * out of one is force-quitting.
 *
 * ── THE PROMPT IS NOT DISMISSABLE, AND STILL NOT AUTOMATIC ──────────────────
 * No backdrop or Escape dismissal, because a staff member who taps past it
 * stays on an old build indefinitely. But the swap only happens on the button:
 * activating a new worker underneath someone half-way through signing a SWMS,
 * or with an unsent job note open on the Stop sheet, would lose their work.
 */

/** How often to re-check /sw.js for a new build. */
const SW_UPDATE_INTERVAL_MS = 60_000;
/** How long to wait for the worker swap before reloading the page ourselves. */
const UPDATE_FALLBACK_MS = 2_500;

export function UpdateController(): React.JSX.Element | null {
    const [updating, setUpdating] = useState(false);
    const [newVersion, setNewVersion] = useState<string | null>(null);

    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(swUrl, registration) {
            if (registration === undefined) {
                return;
            }
            void registration.update();
            setInterval(() => {
                // An update check while offline just logs a failed fetch.
                if (registration.installing !== null || !navigator.onLine) {
                    return;
                }
                void fetch(swUrl, { cache: 'no-store' }).then((res) => {
                    if (res.status === 200) {
                        return registration.update();
                    }
                    return undefined;
                });
            }, SW_UPDATE_INTERVAL_MS);
        },
        onRegisterError(error: unknown) {
            console.error('[sw] registration failed:', error);
        },
    });

    // Only for the version number on the dialog; the worker already decided
    // there is an update.
    useEffect(() => {
        if (!needRefresh) {
            return;
        }
        let cancelled = false;
        fetch(`/version.json?t=${String(Date.now())}`, { cache: 'no-store' })
            .then((res) => (res.ok ? (res.json() as Promise<{ version?: string }>) : null))
            .then((data) => {
                if (!cancelled && data?.version !== undefined) {
                    setNewVersion(data.version);
                }
            })
            .catch(() => {
                /* The dialog reads fine without it. */
            });
        return () => {
            cancelled = true;
        };
    }, [needRefresh]);

    if (!needRefresh) {
        return null;
    }

    return (
        <Dialog
            open
            maxWidth="sm"
            fullWidth
            disableScrollLock
            disableEscapeKeyDown
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') {
                    return;
                }
            }}
            slotProps={{ paper: { sx: { borderRadius: 3, p: 2 } } }}
        >
            <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <SystemUpdateAltIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                    <Typography variant="h5" fontWeight={700}>
                        {newVersion === null
                            ? 'A new version of Silverline is ready'
                            : `Good news! Silverline v${newVersion} is ready`}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ textAlign: 'center', fontSize: '1.05rem', py: 2 }}>
                    Tap Update Now to load the latest version.
                    <br />
                    You are on v{__APP_VERSION__}.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                    variant="contained"
                    size="large"
                    disabled={updating}
                    startIcon={<SystemUpdateAltIcon />}
                    onClick={() => {
                        setUpdating(true);
                        // Ask the waiting worker to take over. vite-plugin-pwa
                        // reloads the page itself once `controllerchange` fires.
                        void updateServiceWorker(true).catch((err: unknown) => {
                            console.error('[sw] update failed:', err);
                        });
                        // ...but it does not always fire. An installed iOS PWA
                        // can swallow it, and if there is no worker in `waiting`
                        // (it already activated, or the registration went away)
                        // the SKIP_WAITING message has nobody to go to. Either
                        // way the promise settles, no reload happens, and the
                        // button stays disabled with no way out — which is what
                        // happened in the field: the only escape was killing the
                        // app. Reload ourselves if nothing has taken over by
                        // then. A reload is safe whatever state we are in: worst
                        // case it comes back on the same version and prompts
                        // again, which is still a working app.
                        setTimeout(() => {
                            window.location.reload();
                        }, UPDATE_FALLBACK_MS);
                    }}
                    sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 700 }}
                >
                    {updating ? 'Updating…' : 'Update Now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
